import React, { useEffect, useState } from "react";
import "../scss/index.scss";
import { CSSTransition } from "react-transition-group";
import { useSelector, useDispatch } from "react-redux";
import {
  getWithdrawHistory,
  getActiveWallet,
  getVolumes,
  withdrawFees,
  apiRequestLimits,
  get2FAStatus,
} from "../store/actions/";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faPenToSquare } from "@fortawesome/free-solid-svg-icons";
import DataLoader from "../components/globalView/DataLoader";
import useWindowSize from "../utils/useWindowSize";
import TableDataWithdraws from "../components/tables/TableDataWithdraws";
import MonthlyProfitsChart from "../components/charts/MonthlyProfitsChart";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { changePage } from "../store/actions";
import LazyLoad from "react-lazyload";

// Partner earnings accrue and pay out in USDC only; the legacy RUNE / CACAO buckets are gone.
const BALANCE_COINS = [{ key: "usdc", label: "USDC", chain: "USD Coin" }];

const Balances = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const sized = useWindowSize();

  const [tableOpen, setTableOpen] = useState("");
  const [checkPending, setCheckPending] = useState(false);
  const [withdrawCode, setWithdrawCode] = useState("");
  const [withdrawError, setWithdrawError] = useState("");

  const withdrawHistory = useSelector(
    (state) => state.userReducer.withdrawHistory
  );
  const volumes = useSelector((state) => state.userReducer.volumes);
  const activeWallet = useSelector((state) => state.userReducer.activeWallet);
  const twoFactor = useSelector((state) => state.userReducer.twoFactor);
  const withdrawFeesMsg = useSelector(
    (state) => state.userReducer.withdrawFeesMsg
  );
  const isLoading = useSelector((state) => state.userReducer.loading);

  useEffect(() => {
    dispatch(getActiveWallet());
    dispatch(getVolumes());
    dispatch(apiRequestLimits());
    dispatch(get2FAStatus());
    fetchWithdrawHistory();
    // eslint-disable-next-line
  }, [dispatch]);

  useEffect(() => {
    if (withdrawHistory?.length > 0 && withdrawHistory[0].status === "pending") {
      setCheckPending(true);
    } else if (withdrawHistory?.length > 0 && withdrawHistory[0].status === "done") {
      dispatch(getVolumes());
    } else {
      setCheckPending(false);
    }
    // eslint-disable-next-line
  }, [withdrawHistory]);

  useEffect(() => {
    if (checkPending) {
      const interval = setInterval(() => {
        fetchWithdrawHistory();
      }, 5000);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line
  }, [checkPending]);

  const fetchWithdrawHistory = async () => {
    try {
      const response = await dispatch(getWithdrawHistory());
      if (response?.length > 0 && response[0].status !== "pending") {
        setCheckPending(false);
      }
    } catch (error) {
      console.error("Failed to fetch withdraw history", error);
    }
  };

  const currentDiv = (id) => {
    setTableOpen(id);
  };

  const withdrawCurrency = volumes?.withdrawCurrency || "USDC";
  const totalBalanceInUsd = parseFloat(volumes?.totalBalanceInUsd || 0);
  const canWithdraw = totalBalanceInUsd >= 100;

  const requestWithdrawFees = async () => {
    if (!canWithdraw) {
      console.error("Balance below the minimum withdrawal threshold");
      return;
    }
    const code = (withdrawCode || "").trim();
    if (!/^[0-9]{6}$/.test(code)) {
      setWithdrawError(
        "Enter the 6-digit code from your authenticator app to confirm the withdrawal."
      );
      return;
    }
    setWithdrawError("");
    try {
      const response = await dispatch(withdrawFees(totalBalanceInUsd, code));
      if (
        response ===
        "Request Sent!, Your withdraw request is pending and it will be viewed by our team to be proceeded"
      ) {
        setWithdrawCode("");
        fetchWithdrawHistory();
        setCheckPending(true);
      }
      console.log(response);
    } catch (err) {
      setWithdrawError(err?.message || "Failed to request withdrawal.");
      console.error("Failed to request withdraw fees", err);
    }
  };

  const handleWithdrawWalletRedirect = () => {
    dispatch(changePage("edit-wallet"));
    navigate("/edit-wallet");
  };

  const isDesktop = sized.width > 900;

  return (
    <>
      <Helmet>
        <title>Balances | Instaswap Partners</title>
      </Helmet>
      <CSSTransition
        timeout={{
          enter: 8500,
          exit: 500,
        }}
        classNames="item"
        key="balances"
      >
        <div>
          <div className="secHdr">
            <h2 className="secTitle">Balances</h2>
            <p className="secLead">
              Your accrued partner earnings in USDC, and your withdraw history.
            </p>
          </div>

          {/* Per-asset balances */}
          <div className="balGrid">
            {BALANCE_COINS.map((coin) => {
              const data = volumes?.balances?.[coin.key];
              return (
                <div className="balCard" key={coin.key}>
                  <div className="balTop">
                    <span className="balIco">
                      {data?.icon ? (
                        <LazyLoad height={36} offset={100}>
                          <img src={data.icon} alt={coin.label} />
                        </LazyLoad>
                      ) : (
                        <FontAwesomeIcon icon={faSpinner} spin color="#7c847e" />
                      )}
                    </span>
                    <div>
                      <div className="balName">{coin.label}</div>
                      <div className="balChain">{coin.chain}</div>
                    </div>
                  </div>
                  <div className="balAmt">
                    {data ? Number(data.balance).toFixed(2) : "—"}
                    <span>{coin.label}</span>
                  </div>
                  <div className="balUsd">
                    {data ? `$${data.balanceInUsd.toFixed(2)}` : ""}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Withdraw panel */}
          <div className="wdPanel">
            <div className="wdL">
              <div className="l">Available to withdraw</div>
              <div className="wdAmt">
                {totalBalanceInUsd.toFixed(2)} {withdrawCurrency}
              </div>
              <div className="wdAddr">
                to{" "}
                {activeWallet ? (
                  <span className="a" onClick={handleWithdrawWalletRedirect}>
                    {activeWallet.slice(0, 4).toUpperCase()}...
                    {activeWallet.slice(-4).toUpperCase()}
                  </span>
                ) : (
                  <span
                    className="a"
                    onClick={handleWithdrawWalletRedirect}
                    style={{ color: "#3ad17e" }}
                  >
                    + Add wallet address
                  </span>
                )}
                <span
                  className="edit"
                  onClick={handleWithdrawWalletRedirect}
                  title="Edit withdraw wallet"
                  aria-label="Edit withdraw wallet"
                >
                  <FontAwesomeIcon icon={faPenToSquare} />
                </span>
              </div>
            </div>
            <div className="wdR">
              {activeWallet && !twoFactor.loaded && (
                <button type="button" className="btnPrimary" disabled>
                  <FontAwesomeIcon icon={faSpinner} spin />
                </button>
              )}
              {activeWallet && twoFactor.loaded && !twoFactor.enabled && (
                <>
                  <div className="wdMin" style={{ maxWidth: 220 }}>
                    Enable 2FA to withdraw.
                  </div>
                  <button
                    type="button"
                    className="btnPrimary"
                    onClick={() => navigate("/security")}
                  >
                    Set up 2FA
                  </button>
                </>
              )}
              {activeWallet && twoFactor.loaded && twoFactor.enabled && (
                <>
                  <input
                    className="input mono"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    placeholder="2FA code"
                    value={withdrawCode}
                    onChange={(e) =>
                      setWithdrawCode(e.target.value.replace(/\D/g, ""))
                    }
                    disabled={!canWithdraw || isLoading}
                    style={{ maxWidth: 140, textAlign: "center" }}
                  />
                  <button
                    type="button"
                    className="btnPrimary"
                    onClick={requestWithdrawFees}
                    disabled={!canWithdraw || isLoading}
                  >
                    {isLoading ? (
                      <>
                        Withdraw <FontAwesomeIcon icon={faSpinner} spin />
                      </>
                    ) : (
                      "Withdraw funds"
                    )}
                  </button>
                </>
              )}
              <div className="wdMin">
                Minimum withdrawal 100 {withdrawCurrency}
              </div>
            </div>
            {!canWithdraw && (
              <div
                className="notice"
                style={{ width: "100%", marginTop: 4 }}
              >
                <span className="ni">⚠</span>
                <div>
                  You will be able to withdraw after your available balance is at
                  least 100 {withdrawCurrency}.
                </div>
              </div>
            )}
            {withdrawError && (
              <div className="notice red" style={{ width: "100%", marginTop: 4 }}>
                <span className="ni">⚠</span>
                <div>{withdrawError}</div>
              </div>
            )}
          </div>

          {withdrawFeesMsg && (
            <div className="notice green" style={{ marginTop: 16 }}>
              <span className="ni">✓</span>
              <div>{withdrawFeesMsg}</div>
            </div>
          )}

          {/* Profits earned chart */}
          {withdrawHistory &&
            withdrawHistory.some((item) => item.status === "done") && (
              <>
                <h3 className="secH">Profits Earned</h3>
                <div className="panel">
                  <MonthlyProfitsChart withdrawHistory={withdrawHistory} />
                </div>
              </>
            )}

          {/* Withdraw history */}
          <h3 className="secH">Withdraw History</h3>
          {withdrawHistory && withdrawHistory.length > 0 ? (
            isDesktop ? (
              <div className="gtScroll">
                <div style={{ minWidth: 940 }}>
                  <div
                    className="gtHead"
                    style={{
                      gridTemplateColumns:
                        "90px 150px minmax(220px,1.4fr) 170px 130px 130px 150px",
                    }}
                  >
                    <span>ID</span>
                    <span>Amount</span>
                    <span>Wallet</span>
                    <span>Date</span>
                    <span>Status</span>
                    <span>Profits</span>
                    <span className="r">Transaction</span>
                  </div>
                  {withdrawHistory.map((item) => (
                    <TableDataWithdraws
                      key={item.id}
                      item={item}
                      tableOpen={tableOpen}
                      currentDiv={currentDiv}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div>
                {withdrawHistory.map((item) => (
                  <TableDataWithdraws
                    key={item.id}
                    item={item}
                    tableOpen={tableOpen}
                    currentDiv={currentDiv}
                  />
                ))}
              </div>
            )
          ) : (
            <div className="gtScroll">
              <div className="gtEmpty">No withdrawals yet</div>
            </div>
          )}
        </div>
      </CSSTransition>
    </>
  );
};

export default Balances;
